import { getInitialPageFromDOM } from '@inertiajs/core';
import { router } from '@inertiajs/react';

/**
 * Browserseitige Nutzungserfassung (Stufe 2 der Nutzungsstatistik).
 *
 * Sammelt Bedienereignisse (Klicks, Filter, Dialoge, Seitenzeit, Fehler,
 * Lebenszeichen) im Speicher und schickt sie gebündelt an POST
 * /nutzung/sammeln. Startet ausschließlich, wenn der Server das über das
 * geteilte Inertia-Prop "activityTracking" freigibt (fail closed) - fehlt
 * das Prop oder ist es serverseitig aus, registriert dieses Modul keinen
 * einzigen Listener und keinen Timer. Jeder Schritt läuft in try/catch:
 * die Erfassung darf die Bedienung nie stören.
 */

const SAMMEL_ROUTE = '/nutzung/sammeln';
const SITZUNGS_SCHLUESSEL = 'nutzung_sitzung_id';
const SENDE_INTERVALL_MS = 15000;
const PULS_INTERVALL_MS = 60000;
const PULS_INTERAKTIONS_FENSTER_MS = 120000;
const PUFFER_SOFORT_GRENZE = 40;
const FILTER_ENTPRELLUNG_MS = 500;

// Deckel für die gemeldete Seitenzeit (Fund vom 19.08.2026): schliesseSeitenzeit()
// rechnete bislang schlicht performance.now() - seiteStartZeit, ganz ohne
// Obergrenze - eine über Nacht offen gelassene, nie geschlossene Seite lieferte
// beim nächsten Wechsel oder Schließen einen einzigen "seitenzeit"-Wert von
// mehreren Stunden. Der Server kappt in
// App\Http\Controllers\Nutzung\ActivitySammelController::pruefeDauer() zwar
// hart bei einer Stunde (3.600.000 ms) - das ist aber nur ein Sicherheitsventil
// gegen manipulierte oder kaputte Werte, kein sinnvoller fachlicher Wert: genau
// diese Rundung auf exakt 3.600.000 ms war live in den Daten sichtbar und
// täuschte eine durchgehend einstündige Bearbeitung EINER Seite vor. 30 Minuten
// sind großzügig genug für eine echte, ununterbrochene Bearbeitung (z. B. eine
// lange Bestellprüfung) und trotzdem kurz genug, um ein vergessenes Tab
// zuverlässig von echtem Arbeiten zu unterscheiden - deutlich unter dem
// serverseitigen Sicherheitsventil, damit der Deckel hier greift, BEVOR der
// unplausible Wert überhaupt verschickt wird.
const MAX_SEITENZEIT_MS = 30 * 60 * 1000;

// Bereichszuordnung spiegelt Abschnitt 5 der Nutzungsstatistik-Spezifikation
// (config/activity.php auf dem Server). Bewusst dupliziert statt per Abruf
// geladen, damit ein einzelnes Klickereignis nicht auf eine Netzwerkantwort
// warten muss - ändert sich die Zuordnung serverseitig, muss sie hier
// nachgezogen werden.
const BEREICHS_PRAEFIXE = [
    { schluessel: ['order'], bereich: 'bestellungen' },
    { schluessel: ['stock'], bereich: 'lager' },
    { schluessel: ['statistics'], bereich: 'verkaufsstatistik' },
    { schluessel: ['users', 'roles', 'departments'], bereich: 'verwaltung' },
    { schluessel: ['campaign', 'mail'], bereich: 'kampagnen' },
    { schluessel: ['support', 'ticket'], bereich: 'support' },
    { schluessel: ['roadmap'], bereich: 'roadmap' },
    { schluessel: ['import'], bereich: 'import' },
    { schluessel: ['item', 'product'], bereich: 'artikel' },
    { schluessel: ['settings'], bereich: 'einstellungen' },
    { schluessel: ['nutzung'], bereich: 'nutzung' },
];

const KLICKBARE_AUSWAHL = 'button, a, [role="button"], .nav-link';
const FILTER_AUSWAHL = 'input, select, textarea, [data-filter]';

// Modulweiter Zustand - existiert nur, nachdem aktiviereErfassung() gelaufen
// ist. Vorher bleibt alles unangefasst (siehe Grundsatz oben).
let sitzungsId = null;
let aktuelleRoute = null;
let aktuellerBereich = 'sonstiges';
let puffer = [];
let sendeTimer = null;
let pulsTimer = null;
let letzteInteraktionAt = 0;
let seiteStartZeit = 0;
let sichtbareZeitMs = 0;
let sichtbarSeit = null;
let letzterFehlerSchluessel = '';
let letzterFehlerZeit = 0;
const eingabeTimer = new WeakMap();
const offeneDialoge = new WeakSet();
let domBeobachter = null;

/**
 * Startpunkt - einmalig aus app.jsx nach dem Mounten aufrufen.
 *
 * Behobener Mangel: Der Sammler prüfte die Freigabe ("activityTracking")
 * bislang AUSSCHLIESSLICH im HTML des allerersten Seitenaufrufs. Wer seine
 * Oberfläche geladen hatte, bevor die Freigabe (Einstellung oder ein neues
 * Deployment) aktiv wurde, blieb dadurch für die gesamte Browsersitzung
 * ungetrackt - selbst wenn jede spätere Inertia-Antwort das Merkmal
 * längst korrekt als "true" mitlieferte (HandleInertiaRequests::share()
 * liefert es bei JEDER Anfrage, nicht nur beim ersten Laden). Ist die
 * Freigabe beim Start noch aus, hört dieses Modul deshalb zusätzlich auf
 * jede folgende Inertia-Navigation und aktiviert sich selbst, sobald die
 * Freigabe dort erscheint - ohne dass jemand die Seite neu laden muss.
 *
 * Für Oberflächen mit wirklich veraltetem JavaScript (aus der Zeit vor
 * diesem Deployment) sorgt Inertias eingebauter Versionsabgleich für das
 * eigentliche Auffrischen: weicht die im Client gemerkte Asset-Version von
 * der aktuellen ab, wandelt @inertiajs/core die NÄCHSTE Navigation
 * automatisch in einen echten Browser-Sprung zur neuen Seite um (siehe
 * Inertia\Middleware::version() serverseitig, isLocationVisit() im
 * Client). Das greift GENAU beim nächsten Seitenwechsel, den der
 * Mitarbeiter ohnehin auslöst - nie mitten in einer laufenden Eingabe -
 * und lädt dabei zwangsläufig ein frisches Bundle samt frischem HTML.
 * Dafür ist in diesem Modul keine zusätzliche Logik nötig.
 */
export function initNutzungserfassung() {
    try {
        const seite = getInitialPageFromDOM('app');
        if (Boolean(seite?.props?.activityTracking)) {
            aktiviereErfassung(seite);
            return;
        }
    } catch (fehler) {
        // Ausfälle beim Start dürfen die Anwendung nie blockieren.
    }

    hoercheAufSpaeteFreigabe();
}

/**
 * Horcht auf jede Inertia-Navigation und aktiviert die Erfassung, sobald
 * "activityTracking" dort erstmals "true" ist - meldet sich danach wieder
 * ab (aktiviereErfassung() registriert für den laufenden Betrieb ihren
 * eigenen, dauerhaften navigate-Handler).
 */
function hoercheAufSpaeteFreigabe() {
    try {
        const abmelden = router.on('navigate', (ereignis) => {
            try {
                const seite = ereignis?.detail?.page;
                if (Boolean(seite?.props?.activityTracking)) {
                    abmelden();
                    aktiviereErfassung(seite);
                }
            } catch (fehler) {
                // still bleiben - die nächste Navigation prüft erneut.
            }
        });
    } catch (fehler) {
        // Kein Horchen möglich (z. B. Router nicht verfügbar) - dann bleibt
        // die Erfassung für diese Sitzung aus, ohne die Anwendung zu stören.
    }
}

function aktiviereErfassung(seite) {
    sitzungsId = leseOderErzeugeSitzungsId();
    uebernehmeSeite(seite);
    seiteStartZeit = performance.now();
    sichtbareZeitMs = 0;
    sichtbarSeit = document.visibilityState === 'visible' ? performance.now() : null;

    // Passive Listener, damit Scrollen/Tippen der Nutzer nie auf die
    // Erfassung wartet - es wird nirgends preventDefault() aufgerufen.
    document.addEventListener('click', beiKlick, { passive: true });
    document.addEventListener('input', beiEingabe, { passive: true });
    document.addEventListener('change', beiAenderung, { passive: true });
    document.addEventListener('visibilitychange', beiSichtbarkeitswechsel);
    window.addEventListener('pagehide', beiSeiteVerlassen);
    window.addEventListener('error', beiFehler);
    window.addEventListener('unhandledrejection', beiUnbehandelterFehler);

    // Ein MutationObserver auf .modal/.show deckt sowohl klassische
    // Bootstrap-JS-Modals als auch react-bootstrap-Modals ab (die feuern
    // keine bs.modal-Ereignisse) - eine einzige Beobachtung statt zweier
    // parallel gepflegter Erfassungswege.
    if (typeof MutationObserver !== 'undefined') {
        domBeobachter = new MutationObserver(beiDomAenderung);
        domBeobachter.observe(document.body, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    router.on('navigate', (ereignis) => {
        try {
            beiNavigation(ereignis.detail.page);
        } catch (fehler) {
            // still bleiben - eine verpasste Seitenzeit ist verschmerzbar.
        }
    });

    sendeTimer = window.setInterval(sendePuffer, SENDE_INTERVALL_MS);
    pulsTimer = window.setInterval(sendePuls, PULS_INTERVALL_MS);
}

function uebernehmeSeite(seite) {
    aktuelleRoute = seite?.props?.route ?? window.location.pathname;
    aktuellerBereich = ermittleBereich(aktuelleRoute);
}

function ermittleBereich(route) {
    if (!route) {
        return 'sonstiges';
    }
    const praefix = String(route).split(/[./]/)[0]?.toLowerCase();
    const treffer = BEREICHS_PRAEFIXE.find((eintrag) => eintrag.schluessel.includes(praefix));
    return treffer ? treffer.bereich : 'sonstiges';
}

function neueZeit() {
    return new Date().toISOString();
}

function kuerzen(text, maxLaenge) {
    const wert = typeof text === 'string' ? text : String(text ?? '');
    return wert.length > maxLaenge ? wert.slice(0, maxLaenge) : wert;
}

function leseOderErzeugeSitzungsId() {
    try {
        let id = sessionStorage.getItem(SITZUNGS_SCHLUESSEL);
        if (!id) {
            id = erzeugeUuid();
            sessionStorage.setItem(SITZUNGS_SCHLUESSEL, id);
        }
        return id;
    } catch (fehler) {
        // sessionStorage kann in Inkognito-/Embed-Kontexten blockiert sein -
        // dann bekommt die Sitzung eben je Tab-Leben eine eigene Kennung.
        return erzeugeUuid();
    }
}

function erzeugeUuid() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    // Fallback ohne Web-Crypto (ältere Browser).
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (zeichen) => {
        const zufall = (Math.random() * 16) | 0;
        const wert = zeichen === 'x' ? zufall : (zufall & 0x3) | 0x8;
        return wert.toString(16);
    });
}

function leseCsrfToken() {
    try {
        const metaToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (metaToken) {
            return metaToken;
        }
        const cookieZeile = document.cookie.split('; ').find((zeile) => zeile.startsWith('XSRF-TOKEN='));
        if (cookieZeile) {
            return decodeURIComponent(cookieZeile.slice('XSRF-TOKEN='.length));
        }
    } catch (fehler) {
        // ohne Token schlägt der Versand serverseitig fehl - das Sammeln
        // selbst darf daran nicht scheitern.
    }
    return '';
}

function merkeInteraktion() {
    letzteInteraktionAt = Date.now();
}

function reiheEreignisEin(ereignis) {
    puffer.push(ereignis);
    if (puffer.length > PUFFER_SOFORT_GRENZE) {
        sendePuffer();
    }
}

function sendePuffer() {
    if (puffer.length === 0) {
        return;
    }
    const ereignisse = puffer;
    puffer = [];
    try {
        const payload = JSON.stringify({ sitzung: sitzungsId, ereignisse });
        const formular = new FormData();
        formular.append('_token', leseCsrfToken());
        formular.append('payload', payload);

        let gesendet = false;
        if (navigator.sendBeacon) {
            gesendet = navigator.sendBeacon(SAMMEL_ROUTE, formular);
        }
        if (!gesendet) {
            fetch(SAMMEL_ROUTE, {
                method: 'POST',
                body: formular,
                keepalive: true,
                credentials: 'same-origin',
            }).catch(() => {});
        }
    } catch (fehler) {
        // Ein verlorenes Paket ist hinnehmbar (siehe Grundsatz "Erfassung
        // darf die Bedienung nie stören").
    }
}

// --- Seitenzeit ------------------------------------------------------

function aktualisiereSichtbareZeit() {
    if (sichtbarSeit !== null) {
        sichtbareZeitMs += performance.now() - sichtbarSeit;
        sichtbarSeit = document.visibilityState === 'visible' ? performance.now() : null;
    } else if (document.visibilityState === 'visible') {
        sichtbarSeit = performance.now();
    }
}

function schliesseSeitenzeit() {
    aktualisiereSichtbareZeit();
    // Beide Werte auf MAX_SEITENZEIT_MS gedeckelt (siehe Begründung dort) -
    // sichtbare_ms zusätzlich, weil auch eine die ganze Zeit sichtbar
    // belassene, aber unbenutzte Seite (zweiter Monitor, niemand klickt) sonst
    // denselben unrealistischen Ausreißer erzeugen würde.
    const gesamtMs = Math.min(MAX_SEITENZEIT_MS, Math.round(performance.now() - seiteStartZeit));
    const sichtbareMs = Math.min(MAX_SEITENZEIT_MS, Math.round(sichtbareZeitMs));
    reiheEreignisEin({
        art: 'seitenzeit',
        zeit: neueZeit(),
        bereich: aktuellerBereich,
        route: aktuelleRoute,
        bezeichnung: null,
        dauer_ms: gesamtMs,
        details: { sichtbare_ms: sichtbareMs },
    });
}

function beiNavigation(seite) {
    // Erst die verlassene Seite mit ihrer eigenen route/bereich abschließen,
    // danach erst auf die neue Seite umschalten - sonst würde die Seitenzeit
    // fälschlich der neuen Route zugeschrieben.
    schliesseSeitenzeit();
    uebernehmeSeite(seite);
    seiteStartZeit = performance.now();
    sichtbareZeitMs = 0;
    sichtbarSeit = document.visibilityState === 'visible' ? performance.now() : null;
}

function beiSeiteVerlassen() {
    try {
        schliesseSeitenzeit();
        sendePuffer();
    } catch (fehler) {
        // nichts zu retten - der Tab schließt ohnehin gerade.
    }
}

function beiSichtbarkeitswechsel() {
    try {
        aktualisiereSichtbareZeit();
        if (document.visibilityState === 'hidden') {
            sendePuffer();
        }
    } catch (fehler) {}
}

// --- Klicks ------------------------------------------------------------

function istZeileKlickbar(zeile) {
    if (zeile.hasAttribute('data-clickable') || zeile.getAttribute('role') === 'button') {
        return true;
    }
    try {
        // Zeilen mit eigenem Klickverhalten setzen (fast) immer cursor:
        // pointer - eine zusätzliche Markup-Konvention wäre eine Änderung an
        // fremden Dateien und damit außerhalb dieses Auftrags.
        return window.getComputedStyle(zeile).cursor === 'pointer';
    } catch (fehler) {
        return false;
    }
}

function ermittleBezeichnung(element) {
    const ariaLabel = element.getAttribute?.('aria-label');
    if (ariaLabel && ariaLabel.trim()) {
        return kuerzen(ariaLabel.trim(), 80);
    }
    const titel = element.getAttribute?.('title') || element.getAttribute?.('data-tooltip');
    if (titel && titel.trim()) {
        return kuerzen(titel.trim(), 80);
    }
    const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
    return text ? kuerzen(text, 80) : '';
}

function beiKlick(ereignis) {
    try {
        let ziel = ereignis.target?.closest?.(KLICKBARE_AUSWAHL);
        if (!ziel) {
            const zeile = ereignis.target?.closest?.('tr');
            if (zeile && istZeileKlickbar(zeile)) {
                ziel = zeile;
            }
        }
        if (!ziel) {
            return;
        }
        const bezeichnung = ermittleBezeichnung(ziel);
        if (!bezeichnung) {
            // Klicks ohne erkennbare Bezeichnung sind für die Auswertung
            // wertlos (Spec §8) und werden verworfen.
            return;
        }
        merkeInteraktion();
        reiheEreignisEin({
            art: 'klick',
            zeit: neueZeit(),
            bereich: aktuellerBereich,
            route: aktuelleRoute,
            bezeichnung,
            dauer_ms: null,
            details: { element: ziel.tagName.toLowerCase() },
        });
    } catch (fehler) {}
}

// --- Filter/Sortierung ---------------------------------------------------

function istPasswortfeld(element) {
    const typ = (element.getAttribute?.('type') || '').toLowerCase();
    const autoComplete = (element.getAttribute?.('autocomplete') || '').toLowerCase();
    return typ === 'password' || autoComplete.includes('password');
}

function istTextEingabe(element) {
    const tag = element.tagName?.toLowerCase();
    if (tag === 'textarea') {
        return true;
    }
    if (tag !== 'input') {
        return false;
    }
    const typ = (element.getAttribute('type') || 'text').toLowerCase();
    return ['text', 'search', 'email', 'tel', 'number', 'url', ''].includes(typ);
}

function sendeFilterEreignis(element) {
    if (istPasswortfeld(element)) {
        return;
    }
    const feldname = element.name || element.id || element.getAttribute('data-filter') || element.getAttribute('aria-label') || 'unbekannt';
    const wert = kuerzen(element.value ?? '', 120);
    merkeInteraktion();
    reiheEreignisEin({
        art: 'filter',
        zeit: neueZeit(),
        bereich: aktuellerBereich,
        route: aktuelleRoute,
        bezeichnung: kuerzen(feldname, 80),
        dauer_ms: null,
        details: { feld: feldname, wert },
    });
}

function beiEingabe(ereignis) {
    try {
        const element = ereignis.target;
        if (!element?.matches?.(FILTER_AUSWAHL) || !istTextEingabe(element) || istPasswortfeld(element)) {
            return;
        }
        // Texteingaben werden entprellt - nur der fertig getippte Wert zählt
        // (Spec §8), sonst würde jeder Tastenanschlag ein eigenes Ereignis
        // erzeugen.
        window.clearTimeout(eingabeTimer.get(element));
        const timer = window.setTimeout(() => sendeFilterEreignis(element), FILTER_ENTPRELLUNG_MS);
        eingabeTimer.set(element, timer);
    } catch (fehler) {}
}

function beiAenderung(ereignis) {
    try {
        const element = ereignis.target;
        if (!element?.matches?.(FILTER_AUSWAHL) || istTextEingabe(element) || istPasswortfeld(element)) {
            // Texteingaben laufen bereits entprellt über 'input' - hier
            // sonst doppelt gezählt.
            return;
        }
        sendeFilterEreignis(element);
    } catch (fehler) {}
}

// --- Dialoge (Modals) ---------------------------------------------------

function meldeDialog(element, zustand) {
    const titel = element.querySelector('.modal-title')?.textContent?.trim() || element.getAttribute('aria-label') || 'Dialog';
    merkeInteraktion();
    reiheEreignisEin({
        art: 'dialog',
        zeit: neueZeit(),
        bereich: aktuellerBereich,
        route: aktuelleRoute,
        bezeichnung: kuerzen(titel, 80),
        dauer_ms: null,
        details: { zustand },
    });
}

function pruefeDialogElement(element) {
    if (!element.classList?.contains('modal')) {
        return;
    }
    const istOffen = element.classList.contains('show');
    const warOffen = offeneDialoge.has(element);
    if (istOffen && !warOffen) {
        offeneDialoge.add(element);
        meldeDialog(element, 'offen');
    } else if (!istOffen && warOffen) {
        offeneDialoge.delete(element);
        meldeDialog(element, 'geschlossen');
    }
}

function pruefeEntfernteDialoge(knoten) {
    if (!(knoten instanceof Element)) {
        return;
    }
    const kandidaten = knoten.classList?.contains('modal') ? [knoten] : Array.from(knoten.querySelectorAll?.('.modal') || []);
    kandidaten.forEach((element) => {
        if (offeneDialoge.has(element)) {
            offeneDialoge.delete(element);
            meldeDialog(element, 'geschlossen');
        }
    });
}

function beiDomAenderung(mutationen) {
    try {
        mutationen.forEach((mutation) => {
            if (mutation.target instanceof Element) {
                pruefeDialogElement(mutation.target);
            }
            mutation.addedNodes?.forEach((knoten) => {
                if (!(knoten instanceof Element)) {
                    return;
                }
                pruefeDialogElement(knoten);
                knoten.querySelectorAll?.('.modal').forEach(pruefeDialogElement);
            });
            // react-bootstrap entfernt geschlossene Modals meist komplett aus
            // dem DOM statt nur die Klasse "show" zu entfernen - ohne diesen
            // Zweig bliebe das Schließen unentdeckt.
            mutation.removedNodes?.forEach(pruefeEntfernteDialoge);
        });
    } catch (fehler) {}
}

// --- Fehler --------------------------------------------------------------

function sollFehlerGemeldetWerden(schluessel) {
    const jetzt = Date.now();
    if (schluessel === letzterFehlerSchluessel && jetzt - letzterFehlerZeit < 5000) {
        // Endlosschleifen (z. B. wiederholt scheiternde Re-Renders) dürfen
        // den Puffer nicht fluten.
        return false;
    }
    letzterFehlerSchluessel = schluessel;
    letzterFehlerZeit = jetzt;
    return true;
}

function beiFehler(ereignis) {
    try {
        const meldung = kuerzen(ereignis.message || 'Unbekannter Fehler', 120);
        const datei = kuerzen(ereignis.filename || '', 120);
        const zeile = ereignis.lineno || null;
        if (!sollFehlerGemeldetWerden(`${meldung}|${datei}|${zeile}`)) {
            return;
        }
        reiheEreignisEin({
            art: 'fehler',
            zeit: neueZeit(),
            bereich: aktuellerBereich,
            route: aktuelleRoute,
            bezeichnung: meldung,
            dauer_ms: null,
            details: { datei, zeile },
        });
    } catch (fehler) {}
}

function beiUnbehandelterFehler(ereignis) {
    try {
        const grund = ereignis.reason;
        const meldung = kuerzen(typeof grund === 'string' ? grund : grund?.message || 'Unbehandelte Promise-Ablehnung', 120);
        if (!sollFehlerGemeldetWerden(`promise|${meldung}`)) {
            return;
        }
        reiheEreignisEin({
            art: 'fehler',
            zeit: neueZeit(),
            bereich: aktuellerBereich,
            route: aktuelleRoute,
            bezeichnung: meldung,
            dauer_ms: null,
            details: { datei: null, zeile: null },
        });
    } catch (fehler) {}
}

// --- Puls (Lebenszeichen) ------------------------------------------------

function sendePuls() {
    try {
        if (document.visibilityState !== 'visible') {
            return;
        }
        if (Date.now() - letzteInteraktionAt > PULS_INTERAKTIONS_FENSTER_MS) {
            // Ohne echte Interaktion in den letzten zwei Minuten kein Puls -
            // ein über Nacht offenes Fenster darf keine Arbeitszeit erzeugen.
            return;
        }
        reiheEreignisEin({
            art: 'puls',
            zeit: neueZeit(),
            bereich: aktuellerBereich,
            route: aktuelleRoute,
            bezeichnung: null,
            dauer_ms: null,
            details: {},
        });
    } catch (fehler) {}
}
